import React, { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip, Box } from "@mui/material";
import {
  Add,
  DownloadOutlined,
  InfoOutlined,
  ModeEditOutlineTwoTone,
} from "@mui/icons-material";
import { useLazyListLocalTransportQuery } from "store/services/localTransportApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useTablePagination, useAppMeta, useAuth } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import ExportLocalTransport from "./LocalTransportComponents/ExportLocalTransport";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import {
  useLazyGetDDLBranchListQuery,
  useLazyGetDDLDestinationBranchListQuery,
} from "store/services/branchApi";
import { useCustomParams } from "hooks/useCustomParams";

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#FFF3CD", textColor: "#856404" },
    "AUTO APPROVED": { backgroundColor: "#D4EDDA", textColor: "#155724" },
    "MANUAL APPROVED": { backgroundColor: "#D1ECF1", textColor: "#0C5460" },
    REJECTED: { backgroundColor: "#F8D7DA", textColor: "#721C24" },
  };
  return (
    styles[status?.toUpperCase()] || {
      backgroundColor: "#F8F9F9",
      textColor: "#7F8C8D",
    }
  );
};

const LocalTransportListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const { params, appendParams } = useCustomParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const branch = params.branch;
  const toBranch = params.toBranch;

  const [fetchLocalTransports, { data: ltRes, isLoading, isFetching, error }] =
    useLazyListLocalTransportQuery();

  // From Branch — restricted
  const [fetchBranches, { data: branchResponse, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  // To Branch — all branches
  const [fetchDestinationBranches, { data: destinationBranchResponse, isLoading: loadingDestinationBranches }] =
    useLazyGetDDLDestinationBranchListQuery();

  const { isEntity, loggedInUser, viewEntity } = useAuth();

  useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      fetchBranches({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      fetchDestinationBranches({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [fetchBranches, fetchDestinationBranches, viewEntity]);

  const branchOptions = useMemo(() => {
    if (!branchResponse) return [];
    if (Array.isArray(branchResponse)) return branchResponse;
    if (branchResponse.data && Array.isArray(branchResponse.data))
      return branchResponse.data;
    return [];
  }, [branchResponse]);

  const destinationBranchOptions = useMemo(() => {
    if (!destinationBranchResponse) return [];
    if (Array.isArray(destinationBranchResponse)) return destinationBranchResponse;
    if (destinationBranchResponse.data && Array.isArray(destinationBranchResponse.data))
      return destinationBranchResponse.data;
    return [];
  }, [destinationBranchResponse]);

  const selectedBranch = useMemo(
    () => branchOptions.find((b) => b._id === branch) || null,
    [branchOptions, branch]
  );

  const selectedToBranch = useMemo(
    () => destinationBranchOptions.find((b) => b._id === toBranch) || null,
    [destinationBranchOptions, toBranch]
  );

  const columns = useMemo(
    () => [
      {
        name: "LT ID",
        selector: (row) =>
          (row.ltTag || `LT-${row._id?.slice(-6)}`).toUpperCase(),
        cell: (row) => (
          <Link
            to={`/app/crm/local-transport/${row._id}/detail`}
            className="hover:text-blue-600 hover:underline"
          >
            <HighlightedText
              text={(row.ltTag || `LT-${row._id?.slice(-6)}`).toUpperCase()}
              highlight={keyword}
            />
          </Link>
        ),
      },
      {
        name: "Branch",
        cell: (row) => {
          const branches = Array.isArray(row.branch)
            ? row.branch
            : row.branch
              ? [row.branch]
              : [];

          if (branches.length === 0) return "N/A";

          const branchNames = branches
            .map((b) => b?.name || "")
            .filter(Boolean);
          const displayText = branchNames.join(", ");

          return (
            <Box
              sx={{
                maxWidth: 180,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: branchNames.length > 1 ? "pointer" : "default",
              }}
              title={branchNames.length > 1 ? branchNames.join(", ") : ""}
            >
              {displayText || "N/A"}
            </Box>
          );
        },
        sortable: false,
      },
      {
        name: "To Branch",
        cell: (row) => {
          const toBranches = Array.isArray(row.toBranch)
            ? row.toBranch
            : row.toBranch
              ? [row.toBranch]
              : [];

          if (toBranches.length === 0) return "N/A";

          const toBranchNames = toBranches
            .map((b) => b?.name || "")
            .filter(Boolean);
          const displayText = toBranchNames.join(", ");

          return (
            <Box
              sx={{
                maxWidth: 180,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: toBranchNames.length > 1 ? "pointer" : "default",
              }}
              title={toBranchNames.length > 1 ? toBranchNames.join(", ") : ""}
            >
              {displayText || "N/A"}
            </Box>
          );
        },
        sortable: false,
      },
      {
        name: "No. of Sacks",
        selector: (row) => row.noOfSacks || 0,
      },
      {
        name: "Mode",
        selector: (row) => row.transportationMode?.name || "N/A",
      },
      {
        name: "Distance (km)",
        selector: (row) => row.distanceKm,
      },
      {
        name: "Est. Cost",
        selector: (row) => row.estimatedCost || "-",
      },
      {
        name: "Approver",
        selector: (row) => row.approver?.name || "N/A",
      },
      {
        name: "Created At",
        selector: (row) => <AppTableDateTime date={row?.createdAt} />,
      },
      {
        name: "Status",
        cell: (row) => {
          const style = getStatusStyle(row.status);
          return (
            <Box display="flex" justifyContent="flex-end">
              <Chip
                label={row.status?.toUpperCase() || "N/A"}
                sx={{
                  backgroundColor: style.backgroundColor,
                  color: style.textColor,
                  fontWeight: "bold",
                  height: "17px",
                  borderRadius: "12px",
                  width: "auto",
                  padding: "0",
                  border: "none",
                  "& .MuiChip-label": {
                    fontSize: "0.6rem",
                    padding: "3px 5px",
                    lineHeight: "8px",
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        name: "Actions",
        cell: (row) => (
          <>
            <Link to={`/app/crm/local-transport/${row._id}/print`}>
              <IconButton size="small" color="primary" title="Download Card">
                <DownloadOutlined />
              </IconButton>
            </Link>
            <Link to={`/app/crm/local-transport/${row._id}/detail`}>
              <IconButton size="small" color="primary">
                <InfoOutlined />
              </IconButton>
            </Link>
            {row.status === "Pending" && (
              <Link to={`/app/crm/local-transport/${row._id}/edit`}>
                <IconButton size="small">
                  <ModeEditOutlineTwoTone />
                </IconButton>
              </Link>
            )}
          </>
        ),
        ignoreRowClick: true,
        right: true,
      },
    ],
    [keyword]
  );

  useEffect(() => {
    fetchLocalTransports({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(branch && { branch }),
      ...(toBranch && { toBranch }),
      ...(isEntity && { userId: loggedInUser?._id }),
    });
  }, [
    activeTab,
    currentPage,
    perPage,
    keyword,
    branch,
    toBranch,
    fetchLocalTransports,
    isEntity,
    loggedInUser?._id,
  ]);

  useAppMeta(`${activeTab} Local Transports`);

  return (
    <BreadcrumbLayout
      breadcrumbs={[{ title: "Local Transport Request" }]}
      headerActions={<PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={[
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" },
          ]}
          defaultValue={activeTab}
          actions={<ExportLocalTransport />}
        />
        <TableFilter
          searchLabel="LT ID"
          disabled={isLoading || isFetching}
        >
          {/* From Branch — restricted */}
          <BaseAutocomplete
            fullWidth
            label="From Branch"
            options={branchOptions}
            value={selectedBranch}
            onChange={(_, val) => appendParams({ branch: val?._id || "" })}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
            loading={loadingBranches}
          />
          {/* To Branch — all branches */}
          <BaseAutocomplete
            fullWidth
            label="To Branch"
            options={destinationBranchOptions}
            value={selectedToBranch}
            onChange={(_, val) => appendParams({ toBranch: val?._id || "" })}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
            loading={loadingDestinationBranches}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={ltRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={ltRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default LocalTransportListScreen;

const PageActions = () => (
  <Link to="/app/crm/local-transport/create">
    <Button startIcon={<Add />} variant="contained">
      New Request
    </Button>
  </Link>
);